import { useState, useEffect } from 'react'
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Warning as WarningIcon
} from '@mui/icons-material'
import { getPolicyGapAssessment, PolicyGapAssessment } from '../services/api'

interface GapAssessmentProps {
  tenantId: string
}

export default function GapAssessment({ tenantId }: GapAssessmentProps) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [gapData, setGapData] = useState<PolicyGapAssessment | null>(null)

  useEffect(() => {
    console.log('GapAssessment: useEffect triggered, tenantId:', tenantId)
    if (tenantId) {
      loadGapAssessment()
    } else {
      setLoading(false)
      setError('No customer ID provided')
    }
  }, [tenantId])

  const loadGapAssessment = async () => {
    try {
      console.log('GapAssessment: Loading gap assessment for customer', tenantId)
      setLoading(true)
      setError(null)
      const data = await getPolicyGapAssessment(tenantId)
      console.log('GapAssessment: Loaded data:', data)
      
      // Check if the response is an error object instead of gap assessment data
      if (data && typeof data === 'object' && 'error' in data) {
        console.error('GapAssessment: API returned error:', data.error)
        setError(data.error as string)
        setGapData(null)
      } else {
        setGapData(data)
      }
    } catch (err: any) {
      console.error('GapAssessment: Error loading gap assessment:', err)
      console.error('GapAssessment: Error details:', err.response?.data || err.message)
      setError(err.response?.data?.error || err.message || 'Failed to load policy gap assessment')
    } finally {
      setLoading(false)
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return 'error'
      case 'medium':
        return 'warning'
      case 'low':
        return 'info'
      default:
        return 'default'
    }
  }

  const getPlatformChip = (platform: string) => {
    const colors: Record<string, any> = {
      Windows: 'primary',
      macOS: 'secondary',
      Android: 'success',
      iOS: 'info',
      Base: 'default'
    }
    return <Chip label={platform} color={colors[platform] || 'default'} size="small" variant="outlined" />
  }

  if (loading) {
    return (
      <Box>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography variant="body2" sx={{ textAlign: 'center' }}>
          Analyzing your tenant licenses and policies...
        </Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    )
  }

  if (!gapData) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No gap assessment data available
      </Alert>
    )
  }

  const getCoverageColor = (percentage: number) => {
    if (percentage >= 80) return 'success'
    if (percentage >= 50) return 'warning'
    return 'error'
  }

  // Additional safety check
  if (!gapData) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No gap assessment data available
      </Alert>
    )
  }

  return (
    <Box>
      {/* License Detection Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <InfoIcon sx={{ mr: 1, color: 'info.main' }} />
            <Typography variant="h6">Detected Microsoft 365 Licenses</Typography>
          </Box>
          {gapData.detectedLicenses?.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {gapData.detectedLicenses.map((license, index) => (
                <Chip
                  key={index}
                  label={license.friendlyName}
                  color="primary"
                  variant="outlined"
                  icon={<CheckCircleIcon />}
                />
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No licenses detected
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Statistics Section */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="info.main">
              {gapData.totalRecommended}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Recommended
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="success.main">
              {gapData.totalDeployed}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Deployed
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="error.main">
              {gapData.totalGaps}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gaps
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color={`${getCoverageColor(gapData.coveragePercentage)}.main`}>
              {gapData.coveragePercentage.toFixed(0)}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Coverage
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Coverage Progress Bar */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" gutterBottom>
          Policy Coverage
        </Typography>
        <LinearProgress
          variant="determinate"
          value={gapData.coveragePercentage}
          color={getCoverageColor(gapData.coveragePercentage)}
          sx={{ height: 10, borderRadius: 5 }}
        />
      </Box>

      {/* Policy Gaps - Most Important Section */}
      {gapData.totalGaps > 0 && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <WarningIcon sx={{ mr: 1, color: 'warning.main' }} />
              <Typography variant="h6">
                Policy Gaps ({gapData.totalGaps})
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Alert severity="warning" sx={{ mb: 2 }}>
              The following policies are recommended for your licenses but not yet deployed.
            </Alert>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Policy Name</TableCell>
                    <TableCell>Platform</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Priority</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {gapData.gapPolicies.map((gap) => (
                    <TableRow key={gap.policyID}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {gap.policyName}
                        </Typography>
                      </TableCell>
                      <TableCell>{getPlatformChip(gap.platform)}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{gap.policyType}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={gap.priority}
                          color={getPriorityColor(gap.priority)}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Recommended Policies Accordion */}
      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            Recommended Policies ({gapData.totalRecommended})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Policy Name</TableCell>
                  <TableCell>Platform</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Required Service Plans</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {gapData.recommendedPolicies.map((policy) => (
                  <TableRow key={policy.policyID}>
                    <TableCell>
                      <Typography variant="body2">{policy.policyName}</Typography>
                    </TableCell>
                    <TableCell>{getPlatformChip(policy.platform)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{policy.policyType}</Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={policy.requiredServicePlans}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {policy.requiredServicePlans}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Deployed Policies Accordion */}
      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircleIcon sx={{ mr: 1, color: 'success.main' }} />
            <Typography variant="h6">
              Deployed Policies ({gapData.totalDeployed})
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Policy Name</TableCell>
                  <TableCell>Platform</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Deployed Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {gapData.deployedPolicies.map((policy) => (
                  <TableRow key={policy.customerPolicyID}>
                    <TableCell>
                      <Typography variant="body2">{policy.policyName}</Typography>
                    </TableCell>
                    <TableCell>{getPlatformChip(policy.platform)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{policy.policyType}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={policy.deploymentStatus}
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(policy.deployedDate).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}
